// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Loaded, Loadable, LoadingStatus, ResourceAction, StatementService, StatementResource } from '@app/core';
import { PolicyStatementActionsUnion, PolicyStatementActionTypes } from './policy-statement.actions';

export interface PolicyStatementState {
  services?: Loaded<StatementService[]> | Loadable;
  resourceActions?: {
    [serviceId: string]: Loaded<ResourceAction[]> | Loadable;
  };
}

export function policyStatementReducer(
  state: Readonly<PolicyStatementState> = {},
  action: PolicyStatementActionsUnion
): Readonly<PolicyStatementState | undefined> {
  switch (action.type) {
    case PolicyStatementActionTypes.SERVICES_LOADING:
      return {
        ...state,
        services: {
          status: LoadingStatus.LOADING
        }
      };

    case PolicyStatementActionTypes.SERVICES_LOAD_COMPLETE:
      return {
        ...state,
        services: {
          data: action.payload.services,
          status: LoadingStatus.LOAD_COMPLETE
        }
      };

    case PolicyStatementActionTypes.SERVICES_LOAD_FAILED:
      return {
        ...state,
        services: {
          status: LoadingStatus.LOAD_FAILED
        }
      };

    case PolicyStatementActionTypes.RESOURCE_ACTIONS_LOADING:
      return {
        ...state,
        resourceActions: {
          ...(state.resourceActions || {}),
          [action.payload.serviceId]: {
            status: LoadingStatus.LOADING
          }
        }
      };

    case PolicyStatementActionTypes.RESOURCE_ACTIONS_LOAD_COMPLETE:
      return {
        ...state,
        resourceActions: {
          ...(state.resourceActions || {}),
          [action.payload.serviceId]: {
            data: action.payload.resourceActions,
            status: LoadingStatus.LOAD_COMPLETE
          }
        }
      };

    case PolicyStatementActionTypes.RESOURCE_ACTIONS_LOAD_FAILED:
      return {
        ...state,
        resourceActions: {
          ...(state.resourceActions || {}),
          [action.payload.serviceId]: {
            status: LoadingStatus.LOAD_FAILED
          }
        }
      };
  }
  return state;
}

export function getStatementServices(state: PolicyStatementState) {
  return state.services;
}

export function getStatementResources(serviceId: string) {
  return (state: PolicyStatementState) => {
    if (state.resourceActions === undefined || state.resourceActions[serviceId] === undefined) {
      return undefined;
    }
    const resourceActions = state.resourceActions[serviceId].data;
    if (resourceActions === undefined) {
      return undefined;
    }

    const resources: StatementResource[] = resourceActions.map(
      (resourceAction: ResourceAction) => resourceAction.resource
    );
    const resourcesById = resources.reduce(
      (acc, resource) => ({
        ...acc,
        [resource.id]: resource
      }),
      {}
    );
    return Object.values(resourcesById);
  };
}

export function getStatementActions(serviceId: string, resourceId: string) {
  return (state: PolicyStatementState) => {
    if (state.resourceActions === undefined || state.resourceActions[serviceId] === undefined) {
      return undefined;
    }

    const resourceActions = state.resourceActions[serviceId].data;
    if (resourceActions === undefined) {
      return undefined;
    }

    return resourceActions
      .filter((resourceAction: ResourceAction) => resourceAction.resource.id === resourceId)
      .map((resourceAction: ResourceAction) => resourceAction.action);
  };
}

export function getLoadableResourceActions(serviceId: string) {
  return (state: PolicyStatementState) => {
    return state.resourceActions && state.resourceActions[serviceId];
  };
}

export function getResourceActionsNeedLoading(serviceId: string) {
  return (state: PolicyStatementState) => {
    if (state.resourceActions === undefined || state.resourceActions[serviceId] === undefined) {
      return true;
    }
    if (state.resourceActions[serviceId].status === LoadingStatus.LOAD_FAILED) {
      return true;
    }
    return false;
  };
}



// WEBPACK FOOTER //
// ./src/app/access-management/state/policy-statement.reducer.ts